import {
  MultiMapExportAdequacyOutcome,
  MultiMapExportBaseMap,
  MultiMapExportBoundaryLabelOption,
  MultiMapExportBoundaryLayer,
  MultiMapExportBoundaryOption,
  MultiMapExportCountyShading,
  MultiMapExportMapStyleOption,
  MultiMapExportShadingSwatch,
  MultiMapExportSolidFillColor,
  MultiMapExportZoomLevel,
} from "@app/projects/network-assessment/multi-map-export/interfaces";
import { QesColors } from "@app/shared/qes-standards/colors";
import { RegionSetStatusType } from "@app/projects/enumerators";

export const MAP_STYLE_BOUNDARY_LINE_OPTIONS: readonly MultiMapExportBoundaryOption[] = Object.freeze([
  {
    label: "Display State Boundaries",
    layer: MultiMapExportBoundaryLayer.StateBoundaries,
  },
  {
    label: "Display County Boundaries",
    layer: MultiMapExportBoundaryLayer.CountyBoundaries,
  },
  {
    label: "Display Zip Boundaries",
    layer: MultiMapExportBoundaryLayer.ZipCodeBoundaries,
  },
]);

export const MAP_STYLE_BOUNDARY_LABEL_OPTIONS: readonly MultiMapExportBoundaryLabelOption[] = Object.freeze([
  {
    dependsOn: MultiMapExportBoundaryLayer.StateBoundaries,
    label: "Label States",
    layer: MultiMapExportBoundaryLayer.StateLabels,
  },
  {
    dependsOn: MultiMapExportBoundaryLayer.CountyBoundaries,
    label: "Label Counties",
    layer: MultiMapExportBoundaryLayer.CountyLabels,
  },
  {
    dependsOn: MultiMapExportBoundaryLayer.ZipCodeBoundaries,
    label: "Label Zip Codes",
    layer: MultiMapExportBoundaryLayer.ZipCodeLabels,
  },
  {
    dependsOn: MultiMapExportBoundaryLayer.ZipCodeLabels,
    isChildRow: true,
    label: "Display Zip Points",
    layer: MultiMapExportBoundaryLayer.ZipCodePoints,
  },
]);

export const MAP_STYLE_BOUNDARY_LAYER_ORDER: readonly MultiMapExportBoundaryLayer[] = Object.freeze([
  ...MAP_STYLE_BOUNDARY_LINE_OPTIONS.map((option) => option.layer),
  ...MAP_STYLE_BOUNDARY_LABEL_OPTIONS.map((option) => option.layer),
]);

export const MAP_STYLE_BASE_MAP_OPTIONS: readonly MultiMapExportMapStyleOption<MultiMapExportBaseMap>[] = Object.freeze([
  {
    label: "Light Background",
    value: MultiMapExportBaseMap.Light,
  },
  {
    label: "Terrain Background",
    value: MultiMapExportBaseMap.Terrain,
  },
]);

export const MAP_STYLE_ZOOM_OPTIONS: readonly MultiMapExportMapStyleOption<MultiMapExportZoomLevel>[] = Object.freeze([
  {
    hoverDescription: "Frames each map to fit all plotted points, so members and providers outside the county still appear.",
    label: "Zoom to Map Contents",
    value: MultiMapExportZoomLevel.MapContents,
  },
  {
    hoverDescription: "Frames each map to the county boundary, which can crop out points that fall outside the county.",
    label: "Zoom to County",
    value: MultiMapExportZoomLevel.County,
  },
]);

// By County Class is a fifth mode, added by WI 213014.
export const MAP_STYLE_COUNTY_SHADING_OPTIONS: readonly MultiMapExportMapStyleOption<MultiMapExportCountyShading>[] = Object.freeze([
  {
    label: "Solid Color",
    value: MultiMapExportCountyShading.SolidColor,
  },
  {
    label: "By Adequacy Status",
    value: MultiMapExportCountyShading.AdequacyStatus,
  },
  {
    label: "By County Status",
    value: MultiMapExportCountyShading.CountyStatus,
  },
  {
    label: "No Shading",
    value: MultiMapExportCountyShading.NoShading,
  },
]);

export const MAP_STYLE_SOLID_FILL_OPTIONS: readonly MultiMapExportShadingSwatch<MultiMapExportSolidFillColor>[] = Object.freeze([
  {
    color: QesColors.Gray0,
    label: "White",
    value: MultiMapExportSolidFillColor.White,
  },
  {
    color: QesColors.Purple100,
    label: "Lavender",
    value: MultiMapExportSolidFillColor.Lavender,
  },
  {
    color: QesColors.Blue100,
    label: "Light Blue",
    value: MultiMapExportSolidFillColor.Blue,
  },
  {
    color: QesColors.Green100,
    label: "Light Green",
    value: MultiMapExportSolidFillColor.Green,
  },
  {
    color: QesColors.Yellow100,
    label: "Light Yellow",
    value: MultiMapExportSolidFillColor.Yellow,
  },
]);

export const MAP_STYLE_ADEQUACY_SHADING_OPTIONS: readonly MultiMapExportShadingSwatch<MultiMapExportAdequacyOutcome>[] = Object.freeze([
  {
    borderColor: QesColors.Green700,
    color: QesColors.Green100,
    label: "Adequate",
    value: MultiMapExportAdequacyOutcome.Passing,
  },
  {
    borderColor: QesColors.Yellow700,
    color: QesColors.Yellow100,
    label: "Not Adequate (Servicing)",
    value: MultiMapExportAdequacyOutcome.FailingServicingRequirement,
  },
  {
    borderColor: QesColors.Amber700,
    color: QesColors.Amber100,
    label: "Not Adequate (Access)",
    value: MultiMapExportAdequacyOutcome.FailingAccessRequirement,
  },
  {
    borderColor: QesColors.Red700,
    color: QesColors.Red100,
    label: "Not Adequate (Failing both Access and Servicing requirements)",
    value: MultiMapExportAdequacyOutcome.FailingBothRequirements,
  },
]);

export const MAP_STYLE_COUNTY_STATUS_SHADING_OPTIONS: readonly MultiMapExportShadingSwatch<RegionSetStatusType>[] = Object.freeze([
  {
    borderColor: QesColors.Blue700,
    color: QesColors.Blue100,
    label: "Active",
    value: RegionSetStatusType.Active,
  },
  {
    borderColor: QesColors.Yellow700,
    color: QesColors.Yellow100,
    label: "Pending",
    value: RegionSetStatusType.Pending,
  },
  {
    borderColor: QesColors.Green700,
    color: QesColors.Green100,
    label: "Approved",
    value: RegionSetStatusType.Approved,
  },
  {
    borderColor: QesColors.Amber700,
    color: QesColors.Amber100,
    label: "Opportunity",
    value: RegionSetStatusType.Opportunity,
  },
]);
